/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */
import { store } from "..";
import evt from "../event";

import { fetchRadioGroups, radioGroupActions, updateRadioGroups } from "./radioGroupSlice";
import { fetchEdgePools, fetchEdges, fetchEdgeUpgrades } from "./edgeSlice";
import { fetchRadios, fetchRadioUpgrades } from "./radioSlice";
import { fetchDNN, fetchDevices } from "./subscriberSlice";
import { fetchDeviceGroups } from "./deviceGroupSlice";
import { fetchSites } from "./siteSlice";
import { utilityActions } from "./utilitySlice";
import { fetchRadioSimulator, fetchSimulator, fetchUESimulators } from "./simulatorSlice";
import { fetchServiceController } from "./dashboardSlice";
import { fetchInsights } from "./insightSlice";
import { fetchOperations, fetchSupportChannel, fetchSystemSettings, settingsActions } from "./settingsSlice";
import { fetchPolicies, fetchQOSProfiles, fetchZones } from "./zoneSlice";
import { fetchZoneGroups } from "./zoneGroupSlice";
import { fetchHardware } from "./hardwareSlice";
import { workerInstance } from "~/sw";
import { fetchNetwork } from "./networkSlice";
import { fetchClockPools, fetchClocks } from "./syncProfileSlice";
import { fetchMobilityProfiles } from "./mobilityProfileSlice";
import { fetchAccessConfigs, fetchDefaultRadioManager } from "./accessConfigSlice";
import { fetchCACertConfigs } from "./caCertSlice";
import { fetchUsers } from "./userSlice";
import { fetchApiTokens } from "./apiTokenSlice";
import { fetchAAAProfiles, fetchMonitoringProfiles } from "./monitoringProfileSlice";
import { fetchAlertProfiles } from "./alertProfileSlice";
import { fetchMetricsTemplate } from "./kpiTemplateSlice";
import { enqueueSnackbar } from "notistack";
import { fetchAllOperators, fetchOperatorRadios, fetchOperatorsByEdge } from "./operatorSlice";
import { notificationActions } from "./notificationsSlice";
import { getDownloadUrlFromOutput, createNotificationFromOperation, generateNotificationsFromOperations } from "~/components/Notifications/utils";
import { getSessionInitiatedOperations, operationService } from "~/services";

// Flag to pause events for monitoring page when historical data is being shown
export let PAUSE_EVENTS = false;
export const pauseEvents = (value: boolean) => {
  PAUSE_EVENTS = value;
}

// Radio Events
evt.on("refreshRadios", async (data) => {
  if (data) {
    if (PAUSE_EVENTS) return
    const radios = store.getState().radio.completeData;

    // Check if any of the patches contain upgradeToRelease or runtimeInfo.softwareVersion in modifiedValues
    const hasVersionChanges = data.some(patch => {
      if (!patch.modifiedValues || typeof patch.modifiedValues !== 'object') return false;

      // Check for direct upgradeToRelease property
      if ('upgradeToRelease' in patch.modifiedValues) return true;

      // Check for nested runtimeInfo.softwareVersion property
      if (patch.modifiedValues.runtimeInfo &&
        typeof patch.modifiedValues.runtimeInfo === 'object' &&
        'softwareVersion' in patch.modifiedValues.runtimeInfo) {
        return true;
      }

      return false;
    });

    if (hasVersionChanges) {
      // If version-related fields are modified, fetch updateAvailableRadioUpgrades
      store.dispatch(fetchRadioUpgrades());
    }

    const _radios = await workerInstance.patch(radios, data) as typeof radios;
    store.dispatch({
      type: fetchRadios.typePrefix + "/fulfilled",
      payload: _radios
    });

  } else {
    await store.dispatch(fetchRadios());
  }

  // Update radio groups after fetching radios to ensure the radio groups coordinates are updated
  const state = store.getState();
  const groups = state.radioGroup.data;
  const radios = state.radio.data;
  if(!groups.length) {
    console.warn("No groups to update");
    return;
  }
  const _groups = await updateRadioGroups(groups, radios);
  store.dispatch(radioGroupActions.setData(_groups));
});


evt.on("refreshRadioGroups", async (data) => {
  if (data) {
    if (PAUSE_EVENTS) return
    const radioGroups = store.getState().radioGroup.data;
    const _radioGroups = await workerInstance.patch(radioGroups, data) as typeof radioGroups
    store.dispatch({
      type: fetchRadioGroups.typePrefix + "/fulfilled",
      payload: _radioGroups
    });
  } else {
    store.dispatch(fetchRadioGroups({}));
  }
});

evt.on("refreshSites", async (data) => {
  if (data) {
    if (PAUSE_EVENTS) return
    const sites = store.getState().site.data;
    const _sites = await workerInstance.patch(sites, data) as typeof sites;
    store.dispatch({
      type: fetchSites.typePrefix + "/fulfilled",
      payload: _sites
    });
  } else {
    store.dispatch(fetchSites());
  }
});

evt.on("updateGroupAndRadio", async () => {
  const state = store.getState();
  const groups = state.radioGroup.data;
  const radios = state.radio.data;
  const _groups = await updateRadioGroups(groups, radios);
  store.dispatch(radioGroupActions.setData(_groups));
});

// Edge Events
evt.on("refreshEdges", async (data) => {
  if (data) {
    if (PAUSE_EVENTS) return
    const edges = store.getState().edge.data;

    // When state is empty (initial page load), patching produces incorrect results.
    // Fall back to a full HTTP fetch to ensure edges and upgrades are loaded correctly.
    if (edges.length === 0) {
      await store.dispatch(fetchEdges());
      store.dispatch(fetchEdgeUpgrades());
      return;
    }

    // Check if any of the patches contain currentRelease in modifiedValues
    const hasCurrentReleaseChanges = data.some(patch =>
      patch.modifiedValues &&
      typeof patch.modifiedValues === 'object' &&
      ('currentRelease' in patch.modifiedValues || 'upgradeToRelease' in patch.modifiedValues)
    );

    if (hasCurrentReleaseChanges) {
      // If currentRelease is modified, fetch updateAvailableEdgeUpgrades`
      store.dispatch(fetchEdgeUpgrades());
    }
    const _edges = await workerInstance.patch(edges, data) as typeof edges;
    store.dispatch({
      type: fetchEdges.typePrefix + "/fulfilled",
      payload: _edges
    });

  } else {
    await store.dispatch(fetchEdges());
    store.dispatch(fetchEdgeUpgrades());
  }
});

evt.on("refreshEdgePools", async (data) => {
  if (data) {
    if (PAUSE_EVENTS) return
    const edgePools = store.getState().edge.edgePools;
    const _edgePools = await workerInstance.patch(edgePools, data) as typeof edgePools;
    store.dispatch({
      type: fetchEdgePools.typePrefix + "/fulfilled",
      payload: _edgePools
    });
  } else {
    store.dispatch(fetchEdgePools());
  }
});

// Device Events
evt.on("refreshDevices", async (data) => {
  if (data) {
    const _store = store.getState();
    const devices = _store.subscriber.data;
    const radios = _store.radio.data;
    const deviceGroups = _store.deviceGroup.data;

    const _devices = await workerInstance.patch(devices, data) as typeof devices;
    store.dispatch({
      type: fetchDevices.typePrefix + "/fulfilled",
      payload: {
        devices: _devices,
        radios,
        deviceGroups,
      }
    });
  } else {
    store.dispatch(fetchDevices());
  }
});

evt.on("refreshDeviceGroups", async (data) => {
  if (data) {
    const deviceGroups = store.getState().deviceGroup.data;
    const _deviceGroups = await workerInstance.patch(deviceGroups, data) as typeof deviceGroups;
    store.dispatch({
      type: fetchDeviceGroups.typePrefix + "/fulfilled",
      payload: _deviceGroups
    });
  } else {
    store.dispatch(fetchDeviceGroups());
  }
});

evt.on("setStartAndEndTime", (startTime, endTime) => {
  store.dispatch(utilityActions.setStartTime(startTime));
  store.dispatch(utilityActions.setEndTime(endTime));
  store.dispatch(utilityActions.setTimeInterval({ interval: "custom" }));
});


// Simulator Events
evt.on("refreshRadioSimulator", () => {
  store.dispatch(fetchRadioSimulator());
});

evt.on("refreshSimulator", async (data) => {
  if (data) {
    const simulators = store.getState().simulator.data;
    const existingIds = new Set(simulators.flatMap((simulator) => simulator.id ? [simulator.id] : []));
    const normalizedPatches = data.map((patch) => {
      if (patch.eventType !== "CREATE" || !existingIds.has(patch.objectId)) {
        return patch;
      }

      return {
        ...patch,
        eventType: "UPDATE",
      };
    });

    const _simulators = await workerInstance.patch(simulators, normalizedPatches) as typeof simulators;
    const seenIds = new Set<string>();
    const dedupedSimulators = [..._simulators].reverse().filter((simulator) => {
      if (!simulator.id) {
        return true;
      }

      if (seenIds.has(simulator.id)) {
        return false;
      }

      seenIds.add(simulator.id);
      return true;
    }).reverse();

    store.dispatch({
      type: fetchSimulator.typePrefix + "/fulfilled",
      payload: dedupedSimulators
    });
  } else {
    store.dispatch(fetchSimulator());
  }
});

evt.on("refreshUESimulators", async (data) => {
  if (data) {
    const ueSimulators = store.getState().simulator.ueSimulators;
    const _ueSimulators = await workerInstance.patch(ueSimulators, data) as typeof ueSimulators;
    store.dispatch({
      type: fetchUESimulators.typePrefix + "/fulfilled",
      payload: _ueSimulators
    });
  } else {
    store.dispatch(fetchUESimulators());
  }
});

// Profile Events
evt.on("refreshClockPools", async (data) => {
  if (data) {
    const clockPools = store.getState().syncProfile.clockPools;
    const _clockPools = await workerInstance.patch(clockPools, data) as typeof clockPools;
    store.dispatch({
      type: fetchClockPools.typePrefix + "/fulfilled",
      payload: _clockPools
    });
  } else {
    store.dispatch(fetchClockPools());
  }
});

evt.on("refreshClocks", async (data) => {
  if (data) {
    const clocks = store.getState().syncProfile.clocks;
    const _clocks = await workerInstance.patch(clocks, data) as typeof clocks;
    store.dispatch({
      type: fetchClocks.typePrefix + "/fulfilled",
      payload: _clocks
    });
  } else {
    store.dispatch(fetchClocks());
  }
});

evt.on("refreshMobilityProfiles", async (data) => {
  if (data) {
    const mobilityProfiles = store.getState().mobilityProfile.data;
    const _mobilityProfiles = await workerInstance.patch(mobilityProfiles, data) as typeof mobilityProfiles;
    store.dispatch({
      type: fetchMobilityProfiles.typePrefix + "/fulfilled",
      payload: _mobilityProfiles
    });
  } else {
    store.dispatch(fetchMobilityProfiles());
  }
});

evt.on("refreshMonitoringSystem", async (data) => {
  if (data) {
    const monitoringProfiles = store.getState().monitoringProfile.data;
    const _monitoringProfiles = await workerInstance.patch(monitoringProfiles, data) as typeof monitoringProfiles;
    store.dispatch({
      type: fetchMonitoringProfiles.typePrefix + "/fulfilled",
      payload: _monitoringProfiles
    });
  } else {
    store.dispatch(fetchMonitoringProfiles());
  }
});

evt.on("refreshAAAProfiles", async (data) => {
  if (data) {
    const aaaProfiles = store.getState().monitoringProfile.aaa.data;
    const _aaaProfiles = await workerInstance.patch(aaaProfiles, data) as typeof aaaProfiles;
    store.dispatch({
      type: fetchAAAProfiles.typePrefix + "/fulfilled",
      payload: _aaaProfiles
    });
  } else {
    store.dispatch(fetchAAAProfiles());
  }
});

evt.on("refreshAlertProfiles", async (data) => {
  if (data) {
    const alertProfiles = store.getState().alertProfile.data;
    const _alertProfiles = await workerInstance.patch(alertProfiles, data) as typeof alertProfiles;
    store.dispatch({
      type: fetchAlertProfiles.typePrefix + "/fulfilled",
      payload: _alertProfiles
    });
  } else {
    store.dispatch(fetchAlertProfiles());
  }
});

// Admin Pages Events

evt.on("refreshNetwork", async (data) => {
  if (data) {
    const network = store.getState().network.data;
    if (network) {
      const _network = await workerInstance.patch([network], data) as typeof network;
      store.dispatch({
        type: fetchNetwork.typePrefix + "/fulfilled",
        payload: _network
      });
    }
  } else {
    store.dispatch(fetchNetwork());
  }
});

evt.on("refreshUsers", async (data) => {
  if (data) {
    const users = store.getState().user.data;
    const _users = await workerInstance.patch(users, data) as typeof users;
    store.dispatch({
      type: fetchUsers.typePrefix + "/fulfilled",
      payload: _users
    });
  } else {
    store.dispatch(fetchUsers());
  }
});

evt.on("refreshSettings", async (data) => {
  if (data) {
    const settings = store.getState().settings.system
    const _settings = await workerInstance.patch(settings, data) as typeof settings;
    store.dispatch({
      type: fetchSystemSettings.typePrefix + "/fulfilled",
      payload: _settings
    });
  } else {
    store.dispatch(fetchSystemSettings());
  }
});


evt.on("refreshAccessConfig", async (data) => {
  if (data) {
    const accessConfig = store.getState().accessConfig.data;
    const _accessConfig = await workerInstance.patch(accessConfig, data) as typeof accessConfig;
    store.dispatch({
      type: fetchAccessConfigs.typePrefix + "/fulfilled",
      payload: _accessConfig
    });
  } else {
    store.dispatch(fetchAccessConfigs());
  }
});

evt.on("refreshDefaultRadioManager", async (data) => {
  if (data) {
    const defaultRadioManager = store.getState().accessConfig.defaultRadioManager;
    const _defaultRadioManager = await workerInstance.patch(defaultRadioManager, data) as typeof defaultRadioManager;
    store.dispatch({
      type: fetchDefaultRadioManager.typePrefix + "/fulfilled",
      payload: _defaultRadioManager
    });
  } else {
    store.dispatch(fetchDefaultRadioManager());
  }
});

evt.on("refreshCACert", async (data) => {
  if (data) {
    const caCerts = store.getState().caCert.data;
    const _caCerts = await workerInstance.patch(caCerts, data) as typeof caCerts;
    store.dispatch({
      type: fetchCACertConfigs.typePrefix + "/fulfilled",
      payload: _caCerts
    });
  } else {
    store.dispatch(fetchCACertConfigs());
  }
});

evt.on("refreshApiToken", async (data) => {
  if (data) {
    const apiToken = store.getState().apiToken.data;
    const _apiToken = await workerInstance.patch(apiToken, data) as typeof apiToken;
    store.dispatch({
      type: fetchApiTokens.typePrefix + "/fulfilled",
      payload: _apiToken
    });
  } else {
    store.dispatch(fetchApiTokens());
  }
});

evt.on('refreshSupportChannel', async (data) => {
  if (data) {
    const supportChannel = store.getState().settings.support;
    const _supportChannel = await workerInstance.patch([supportChannel], data) as typeof supportChannel[];
    store.dispatch({
      type: fetchSupportChannel.typePrefix + "/fulfilled",
      payload: _supportChannel
    });
  } else {
    store.dispatch(fetchSupportChannel());
  }
});

evt.on("refreshDNN", async (data) => {
  if (data) {
    const dnns = store.getState().subscriber.networksData;
    const _dnns = await workerInstance.patch(dnns, data) as typeof dnns;
    store.dispatch({
      type: fetchDNN.typePrefix + "/fulfilled",
      payload: _dnns
    });
  } else {
    store.dispatch(fetchDNN());
  }
});

evt.on("refreshMetricsTemplate", async (data) => {
  if (data) {
    const metricTemplates = store.getState().kpiTemplate.data;
    const _metricTemplates = await workerInstance.patch(metricTemplates, data) as typeof metricTemplates;
    store.dispatch({
      type: fetchMetricsTemplate.typePrefix + "/fulfilled",
      payload: _metricTemplates
    });
  } else {
    store.dispatch(fetchMetricsTemplate());
  }
});

evt.on("openSnackbar", (data) => {
  enqueueSnackbar(data.message, {
    variant: data.severity
  })
});

evt.on("refreshServiceController", () => {
  store.dispatch(fetchServiceController());
});


evt.on("refreshInsights", (status, sourceType, sourceId) => {

  store.dispatch(fetchInsights({
    sourceType: sourceType,
    status: status,
    source: sourceId ? { id: sourceId } as any : undefined
  }));
});

evt.on('refreshSystemSettings', () => {
  store.dispatch(fetchSystemSettings());
});

evt.on('refreshQOSProfile', async (data) => {
  if (data) {
    const qosProfiles = store.getState().zone.qosProfiles.data;
    const qciData = store.getState().zone.qosProfiles.qciData;
    const _qosProfiles = await workerInstance.patch(qosProfiles, data) as typeof qosProfiles;
    store.dispatch({
      type: fetchQOSProfiles.typePrefix + "/fulfilled",
      payload: { qos: _qosProfiles, qci: qciData }
    });
  } else {
    store.dispatch(fetchQOSProfiles());
  }
});


evt.on('refreshPolicy', async (data) => {
  if (data) {
    const policies = store.getState().zone.policies.data;
    const _policies = await workerInstance.patch(policies, data) as typeof policies;
    store.dispatch({
      type: fetchPolicies.typePrefix + "/fulfilled",
      payload: _policies
    });
  } else {
    store.dispatch(fetchPolicies());
  }
});

evt.on('refreshVMZone', async (data) => {
  if (data) {
    const zones = store.getState().zone.data;
    const _zones = await workerInstance.patch(zones, data) as typeof zones;
    store.dispatch({
      type: fetchZones.typePrefix + "/fulfilled",
      payload: _zones
    });
  } else {
    store.dispatch(fetchZones());
  }
});

evt.on('refreshZoneGroup', async (data) => {
  if (data) {
    const zoneGroups = store.getState().zoneGroup.data;
    const _zoneGroups = await workerInstance.patch(zoneGroups, data) as typeof zoneGroups;
    store.dispatch({
      type: fetchZoneGroups.typePrefix + "/fulfilled",
      payload: _zoneGroups
    });
  } else {
    store.dispatch(fetchZoneGroups());
  }
});

evt.on('refreshHardware', async (data) => {
  if (data) {
    const hardware = store.getState().hardware.data;
    const _hardware = await workerInstance.patch(hardware, data) as typeof hardware;
    store.dispatch({
      type: fetchHardware.typePrefix + "/fulfilled",
      payload: _hardware
    });
  } else {
    store.dispatch(fetchHardware());
  }
});

evt.on('refreshOperators', async (data) => {
  if (data) {
    const operatorsAll = store.getState().operator.data;
    const _operatorsAll = await workerInstance.patch(operatorsAll, data) as typeof operatorsAll;
    store.dispatch({
      type: fetchAllOperators.typePrefix + "/fulfilled",
      payload: _operatorsAll
    });

    const operatorsByEdge = store.getState().operator.edgeOperators;
    const _operatorsByEdge = await workerInstance.patch(operatorsByEdge, data) as typeof operatorsByEdge;
    store.dispatch({
      type: fetchOperatorsByEdge.typePrefix + "/fulfilled",
      payload: _operatorsByEdge
    });

  } else {
    // This will be handled locally by each components using operator.
  }
});

evt.on('refreshOperatorRadios', async (data) => {
  if (data) {
    const operatorRadios = store.getState().operator.radio.data;
    const _operatorRadios = await workerInstance.patch(operatorRadios, data) as typeof operatorRadios;
    store.dispatch({
      type: fetchOperatorRadios.typePrefix + "/fulfilled",
      payload: _operatorRadios
    });
  }
});
const downloadingOperations = new Set<string>();

evt.on('operations', async (id, data) => {
  if (data) {
    const operations = store.getState().settings.operations;
    const _operations = await workerInstance.patch(operations, [data]) as typeof operations;
    // Use patchOperations, so a late REST reload cannot stomp this update
    store.dispatch(settingsActions.patchOperations(_operations));

    // Handle notification sync
    // Check if notification exists for the operation id
    if (!data.modifiedValues || !id) return;

    const notifications = store.getState().notifications.data;
    const notification = notifications.find(n => n.operationId === id);
    const { status, statusMessage, output, input, completionTime } = data.modifiedValues;
    
    // Get current operation
    const currentOperation = _operations.find((op) => op.id === id);
    
    // Handle automatic download - only for operations initiated in this browser tab/session
    const currentOp = _operations.find(op => op.id === id);
    const isRadioMonitoring = currentOp?.operationType === 'START_RADIO_MONITORING';
    const url = getDownloadUrlFromOutput(output);
    if (url && !isRadioMonitoring) {
      // Check if the operation is already in the downloadingOperations set
      if (downloadingOperations.has(id)) return;

      // Only download if the operation was initiated in the current session
      const sessionOperations = getSessionInitiatedOperations();
      const downloadAllowed = sessionOperations.includes(id);

      if (downloadAllowed) {
        downloadingOperations.add(id);
        await handleAutomaticDownload(id, url);
      }
    }
    if (notification) {
      store.dispatch(notificationActions.syncWithOperation({
        operationId: id,
        operationName: currentOperation?.operationType,
        source: 'websocket',
        status,
        statusMessage,
        output,
        input,
        completionTime,
      }));
    } else {
      // Create notification for new operation
      // if operation is not found, return
      if (!currentOperation) return;

      const notification = createNotificationFromOperation(currentOperation, 'websocket');
      store.dispatch(notificationActions.addNotification(notification));
    }
  } else {
    const data = await store.dispatch(fetchOperations()).unwrap();
    store.dispatch(notificationActions.bulkAddNotifications(generateNotificationsFromOperations(data)));
  }
});


const handleAutomaticDownload = async (operationId: string, url: string) => {
  console.log("handleAutomaticDownload", operationId, url);
    try {
      // Update notification: downloading
      store.dispatch(
        notificationActions.updateNotificationByOperationId({
          operationId,
          updates: {
            subtitle: 'Downloading file...',
            status: 'in-progress',
          },
        })
      );

      const success = await operationService.DownloadFile(url);

      if (success) {
        // Update notification: completed
        store.dispatch(
          notificationActions.updateNotificationByOperationId({
            operationId,
            updates: {
              status: 'completed',
              subtitle: 'File downloaded successfully',
            },
          })
        );
      } else {
        // Update notification: failed
        store.dispatch(
          notificationActions.updateNotificationByOperationId({
            operationId,
            updates: {
              status: 'failed',
              subtitle: 'Download failed',
            },
          })
        );
      }
    } catch (error) {
      console.error('Automatic download error:', error);
      store.dispatch(
        notificationActions.updateNotificationByOperationId({
          operationId,
          updates: {
            status: 'failed',
            subtitle: 'Download error: ' + (error as Error).message,
          },
        })
      );
    }
  }
